'use client';

import * as React from 'react';

import { LoadingDots } from '@/hen-ui/atomics/LoadingDots';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

import {
  type ChatMessageVariantProps,
  chatMessageBodyVariants,
  chatMessageContentVariants,
  chatMessageStatusVariants,
  chatMessageVariants,
} from './chat-message.variants';

export type ChatMessageVariant = NonNullable<
  ChatMessageVariantProps['variant']
>;
export type ChatMessageStatusValue = 'idle' | 'loading' | 'success' | 'error';

interface ChatMessageStatusContextValue {
  variant: ChatMessageVariant;
  status: ChatMessageStatusValue;
}

const ChatMessageStatusContext =
  React.createContext<ChatMessageStatusContextValue | null>(null);

const useChatMessageStatusContext = (component: string) => {
  const ctx = React.useContext(ChatMessageStatusContext);
  if (ctx === null) {
    throw new Error(
      `${component} must be rendered inside a <ChatMessage> parent.`,
    );
  }
  return ctx;
};

export interface ChatMessageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChatMessageVariantProps {
  /**
   * Speaker side. `sender` aligns the bubble to the right (the user),
   * `receiver` aligns it to the left (the assistant or peer).
   */
  variant: ChatMessageVariant;
  /**
   * Lifecycle of the message. Controls what `ChatMessage.Status` renders.
   * Defaults to `idle` (no indicator).
   */
  status?: ChatMessageStatusValue;
  /**
   * Optional pre-formatted timestamp string. Composite owns rendering; the
   * consumer owns formatting (locale, time zone, relative strings, etc.).
   */
  timestamp?: string;
}

export interface ChatMessageAvatarProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Avatar content. Recommended: the hen-ui `<Avatar>` atomic. Any
   * `ReactNode` is accepted so consumers can drop in initials, an icon,
   * or a brand mark.
   */
  children: React.ReactNode;
}

export interface ChatMessageContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Message body. Plain text, rich text, or any `ReactNode` (images,
   * attachment cards, etc.). The bubble owns background, padding, and
   * the default text color via `chatMessageContentVariants`; consumers
   * choose typography. The composite does not parse markdown.
   */
  children: React.ReactNode;
}

export interface ChatMessageStatusProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Consumer-supplied retry node, rendered only when the parent status is
   * `error`. Ignored while the parent status is `loading` (the composite
   * auto-renders `<LoadingDots/>`). Rendered as-is for `idle` / `success`,
   * which lets consumers show e.g. a quiet checkmark on success.
   */
  children?: React.ReactNode;
  /**
   * Optional accessible label forwarded to `LoadingDots` during the
   * `loading` state. Defaults to `"Sending…"`.
   */
  loadingLabel?: string;
}

type ChatMessageComponent = React.ForwardRefExoticComponent<
  ChatMessageProps & React.RefAttributes<HTMLDivElement>
> & {
  Avatar: typeof ChatMessageAvatar;
  Content: typeof ChatMessageContent;
  Status: typeof ChatMessageStatus;
};

const isAvatarChild = (
  child: React.ReactNode,
): child is React.ReactElement<ChatMessageAvatarProps> =>
  React.isValidElement(child) && child.type === ChatMessageAvatar;

const ChatMessageBase = React.forwardRef<HTMLDivElement, ChatMessageProps>(
  (
    { className, variant, status = 'idle', timestamp, children, ...props },
    ref,
  ) => {
    const childArray = React.Children.toArray(children);
    const avatarChild = childArray.find(isAvatarChild);
    const bodyChildren = childArray.filter((child) => !isAvatarChild(child));

    const contextValue = React.useMemo<ChatMessageStatusContextValue>(
      () => ({ variant, status }),
      [variant, status],
    );

    return (
      <ChatMessageStatusContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(chatMessageVariants({ variant }), className)}
          data-hen-id={COMPONENT_IDS.ChatMessage}
          data-variant={variant}
          data-status={status}
          {...props}
        >
          {avatarChild}
          <div className={cn(chatMessageBodyVariants({ variant }))}>
            {bodyChildren}
            {timestamp ? (
              <span className='text-l2 text-subtext' data-slot='timestamp'>
                {timestamp}
              </span>
            ) : null}
          </div>
        </div>
      </ChatMessageStatusContext.Provider>
    );
  },
);
ChatMessageBase.displayName = 'ChatMessage';

const ChatMessageAvatar = React.forwardRef<
  HTMLDivElement,
  ChatMessageAvatarProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('shrink-0', className)}
    data-hen-id='hen.chatMessage.avatar'
    {...props}
  >
    {children}
  </div>
));
ChatMessageAvatar.displayName = 'ChatMessageAvatar';

const ChatMessageContent = React.forwardRef<
  HTMLDivElement,
  ChatMessageContentProps
>(({ className, children, ...props }, ref) => {
  const { variant } = useChatMessageStatusContext('ChatMessage.Content');
  return (
    <div
      ref={ref}
      className={cn(chatMessageContentVariants({ variant }), className)}
      data-hen-id='hen.chatMessage.content'
      {...props}
    >
      {children}
    </div>
  );
});
ChatMessageContent.displayName = 'ChatMessageContent';

const ChatMessageStatus = React.forwardRef<
  HTMLDivElement,
  ChatMessageStatusProps
>(({ className, children, loadingLabel = 'Sending…', ...props }, ref) => {
  const { status } = useChatMessageStatusContext('ChatMessage.Status');

  if (status === 'idle' && children == null) {
    return null;
  }

  let content: React.ReactNode = null;
  if (status === 'loading') {
    content = (
      <span className='inline-flex items-center rounded-full bg-neutral-700 px-xs py-xxs'>
        <LoadingDots label={loadingLabel} size='sm' />
      </span>
    );
  } else if (status === 'error') {
    content = children ?? null;
  } else {
    content = children ?? null;
  }

  if (content == null) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(chatMessageStatusVariants({ status }), className)}
      role='status'
      aria-live='polite'
      data-hen-id='hen.chatMessage.status'
      data-status={status}
      {...props}
    >
      {content}
    </div>
  );
});
ChatMessageStatus.displayName = 'ChatMessageStatus';

const ChatMessage = ChatMessageBase as ChatMessageComponent;
ChatMessage.Avatar = ChatMessageAvatar;
ChatMessage.Content = ChatMessageContent;
ChatMessage.Status = ChatMessageStatus;

export {
  ChatMessage,
  ChatMessageAvatar,
  ChatMessageContent,
  ChatMessageStatus,
};
